import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type {
  FPlusExpressionValue,
  ValidationFieldValue,
} from '../../types/templateModel'
import { generateEmptyAttributeExpressionValue } from '../../uiModels/createEmptyFieldHelpers'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import type { FieldOrigin } from '../../uiModels/types'
import type { ExpressionTypeInformation } from '../fplusExpression/types'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import type { ValidationField } from './ValidationFieldValueTable'

type Props = {
  label: string
  expression: Optional<FPlusExpressionValue>
  onChange?: (nextValue: ValidationFieldValue) => Promise<void>
  value: ValidationFieldValue
  field: ValidationField
  expressionPath: string
  errors: ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  typeInformation?: Optional<ExpressionTypeInformation>
  fromOrigin: Optional<FieldOrigin>
}

export const ValidationFieldValueWithError = ({
  label,
  expression,
  onChange,
  value,
  field,
  expressionPath,
  errors,
  typeInformation,
  fromOrigin,
}: Props) => {
  const { templateVersion } = useTemplateContext()

  assertNotEmpty(templateVersion)

  const handleChangeKeyWithValue = useCallback(
    (nextValue: Optional<FPlusExpressionValue>) => {
      assertNotEmpty(onChange)
      const propertyValues =
        value[field] ??
        generateEmptyAttributeExpressionValue({
          path: value.path,
          fieldName: field,
        })

      return onChange({
        ...value,
        [field]: {
          ...propertyValues,
          expression: nextValue,
          origin: propertyValues.path,
        },
      })
    },
    [field, onChange, value],
  )

  return (
    <div className="flex flex-col gap-y-2">
      <TemplateFieldExpression
        isExpanded
        key={expressionPath}
        expression={expression}
        onChange={handleChangeKeyWithValue}
        path={expressionPath}
        fallbackCode=""
        label={label}
        typeInformation={typeInformation}
        fromOrigin={fromOrigin}
        isEditingInheritedValueAllowed={false}
      />
      {(errors ?? []).map(({ error }) => (
        <div className="text-error-500" key={error.errorMessage}>
          {error.errorMessage}
        </div>
      ))}
    </div>
  )
}
